import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, MapPin, Eye, EyeOff, Home as HomeIcon, Clock, Video, BookmarkPlus, BookmarkCheck } from 'lucide-react';
import { Game } from '@/types';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import {
  getCardPositionBucket,
  getGameAgeBucket,
  getProviderHost,
  trackAnalyticsEvent,
} from '@/lib/analytics';
import { getGameInsights, getRecapPresentation } from '@/lib/gameInsights';
import { getEligibleRecapUrl } from '@/lib/recap';
import { formatDate, formatUtcToLocalDate, formatUtcToLocalTime } from '@/lib/utils';
import TeamLogo from './TeamLogo';
import { motion } from 'framer-motion';
import CompetitionIcon from './CompetitionIcons';
import { useLocalAppState } from '@/contexts/LocalAppStateContext';
import { openTrustedExternalUrl } from '@/lib/platform/externalBrowser';

interface GameCardProps {
  game: Game;
  showScores?: boolean;
  cardIndex?: number;
}

const GameCard: React.FC<GameCardProps> = ({ game, showScores: externalShowScores, cardIndex = 0 }) => {
  const [internalShowScores, setInternalShowScores] = useState(false);
  const { consentSettings } = useCookieConsent();
  const { state: localState, saveGame } = useLocalAppState();
  const isSaved = localState.replayPlan.some((item) => item.id === game.id);
  
  // Update internal state when external state changes
  useEffect(() => {
    if (externalShowScores !== undefined) {
      setInternalShowScores(externalShowScores);
    }
  }, [externalShowScores]);
  
  const isCompleted = game.status.type === 'completed';
  const isSoccer = game.sportType === 'soccer';
  const isNBA = game.sportType === 'nba';
  const isNFL = game.sportType === 'nfl';
  const isMLB = game.sportType === 'mlb';
  const isNHL = game.sportType === 'nhl';
  const recapUrl = getEligibleRecapUrl(game);
  const recapPresentation = getRecapPresentation(recapUrl);
  const insightPills = getGameInsights(game, recapUrl);
  
  // Keep the card date and time tied to the same local instant.
  const localDate = formatUtcToLocalDate(game.utcDatetime) ?? formatDate(game.date);
  const localTime = formatUtcToLocalTime(game.utcDatetime);
  
  // Format team name based on sport type
  const formatTeamName = (team: {city: string, name: string}) => {
    if (isNBA || isNFL || isMLB || isNHL) {
      return `${team.city} ${team.name}`;
    }
    
    return team.name;
  };
  
  const getCardHeaderClass = () => {
    if (isSoccer) return "game-card-header bg-green-900";
    if (isNBA) return "game-card-header bg-orange-600"; // Brighter orange for NBA
    if (isNFL) return "game-card-header bg-amber-900";
    if (isMLB) return "game-card-header bg-blue-900";
    if (isNHL) return "game-card-header bg-cyan-900";
    return "game-card-header skore-gradient-dark";
  };
  
  const toggleScores = () => {
    const nextShowScores = !internalShowScores;
    setInternalShowScores(nextShowScores);

    if (nextShowScores) {
      trackAnalyticsEvent('score_revealed', {
        sport: game.sportType,
        card_position_bucket: getCardPositionBucket(cardIndex),
        has_recap: Boolean(recapUrl),
        game_age_bucket: getGameAgeBucket(game.utcDatetime),
      }, consentSettings.analytics);
    }
  };

  const handleRecapClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!recapUrl) {
      return;
    }

    if (!window.confirm('This external recap may reveal the result. Open it in Safari?')) return;

    trackAnalyticsEvent('recap_clicked', {
      sport: game.sportType,
      card_position_bucket: getCardPositionBucket(cardIndex),
      provider_host: getProviderHost(recapUrl),
      score_revealed_first: internalShowScores,
      game_age_bucket: getGameAgeBucket(game.utcDatetime),
    }, consentSettings.analytics);
    await openTrustedExternalUrl(recapUrl);
  };
  
  // Determine toggle colors based on sport type
  const getToggleColors = () => {
    if (isSoccer) return {
      bgOff: 'bg-green-900/30',
      bgOn: 'bg-green-700',
      border: 'border-green-700'
    };
    if (isNBA) return {
      bgOff: 'bg-orange-600/30',
      bgOn: 'bg-orange-500',
      border: 'border-orange-500'
    };
    if (isNFL) return {
      bgOff: 'bg-amber-900/30',
      bgOn: 'bg-amber-700',
      border: 'border-amber-700'
    };
    if (isMLB) return {
      bgOff: 'bg-blue-900/30',
      bgOn: 'bg-blue-700',
      border: 'border-blue-700'
    };
    if (isNHL) return {
      bgOff: 'bg-cyan-900/30',
      bgOn: 'bg-cyan-700',
      border: 'border-cyan-700'
    };
    return {
      bgOff: 'bg-gray-800',
      bgOn: 'bg-gray-600',
      border: 'border-gray-700'
    };
  };
  
  const toggleColors = getToggleColors();

  const getInsightClasses = (tone: 'metric' | 'trust') => {
    if (tone === 'trust') {
      return 'border-cyan-300/20 bg-cyan-400/10 text-cyan-100';
    }

    return 'border-white/10 bg-white/5 text-gray-200';
  };
  
  return (
    <Card className={`game-card ${isSoccer ? 'soccer-card' : isNBA ? 'nba-card' : isNFL ? 'nfl-card' : isMLB ? 'mlb-card' : isNHL ? 'nhl-card' : ''}`}>
      <CardHeader className={getCardHeaderClass()}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium flex items-center gap-1 text-gray-200">
              <CalendarDays className="h-4 w-4" />
              {localDate}
            </h3>
          </div>
          
          {/* Display competition for soccer */}
          {isSoccer && game.competition && (
            <Badge 
              variant="outline" 
              className="bg-green-900/40 border-green-700 text-green-300 flex items-center gap-1"
              data-custom-name="competition-badge"
            >
              <span className="mr-1">
                <CompetitionIcon competition={game.competition} className="w-5 h-5" />
              </span>
              {game.competition}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-4">
        <div className="flex flex-col space-y-4">
          {/* Reveal scores and recap for completed games with final scores */}
          {isCompleted && game.homeScore !== undefined && game.awayScore !== undefined && (
            <div className="flex justify-between items-center mb-2">
              {/* Game Recap Video Link */}
              {recapUrl ? (
                <a 
                  href={recapUrl}
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(event) => void handleRecapClick(event)}
                  aria-label={`Open external ${recapPresentation?.label.toLowerCase() ?? 'recap'} (may reveal result)`}
                  className={`flex flex-col items-start gap-0.5 leading-tight hover:underline ${
                    isSoccer ? 'text-green-400' : 
                    isNBA ? 'text-orange-300' : 
                    isNFL ? 'text-amber-400' : 
                    isMLB ? 'text-blue-400' : 
                    isNHL ? 'text-cyan-400' :
                    'text-orange-400'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <Video className="h-4 w-4" />
                    <span className="text-sm">{recapPresentation?.label ?? 'Recap available'}</span>
                  </span>
                  <span className="text-[11px] opacity-80">{recapPresentation?.detail ?? 'External link, may reveal result'}</span>
                </a>
              ) : (
                <div></div>
              )}
              
              {/* Toggle Button - only show if not controlled externally */}
              <button
                type="button"
                onClick={toggleScores}
                className={`relative flex h-8 w-14 cursor-pointer items-center justify-center rounded-full border transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${internalShowScores ? toggleColors.bgOn : toggleColors.bgOff} ${toggleColors.border}`}
                role="switch"
                aria-checked={internalShowScores}
                aria-label={internalShowScores ? 'Hide score' : 'Reveal score'}
              >
                <div 
                  className={`absolute left-0.5 transform transition-transform duration-300 bg-white rounded-full w-5 h-5 flex items-center justify-center ${internalShowScores ? 'translate-x-6' : 'translate-x-0.5'}`}
                >
                  {internalShowScores ? 
                    <EyeOff className="h-3 w-3 text-gray-700" /> : 
                    <Eye className="h-3 w-3 text-gray-700" />
                  }
                </div>
              </button>
            </div>
          )}

          {insightPills.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {insightPills.map(({ label, tone }) => (
                <Badge
                  key={label}
                  variant="outline"
                  className={`border px-2.5 py-1 text-[11px] font-medium ${getInsightClasses(tone)}`}
                >
                  {label}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Teams */}
          <div className="space-y-3">
            {/* Away team */}
            <div className="team-row flex justify-between items-center">
              <div className="flex items-center">
                <TeamLogo team={game.awayTeam} sportType={game.sportType} size="md" />
                <span className="font-medium text-gray-200 ml-3">{formatTeamName(game.awayTeam)}</span>
              </div>
              {game.awayScore !== undefined && isCompleted && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: internalShowScores ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`${internalShowScores ? 'visible' : 'invisible'} font-bold text-xl text-gray-200`}
                >
                  {game.awayScore}
                </motion.div>
              )}
            </div>
            
            {/* Home team */}
            <div className="team-row flex justify-between items-center">
              <div className="flex items-center">
                <TeamLogo team={game.homeTeam} sportType={game.sportType} size="md" />
                <div className="ml-3 flex items-center">
                  <span className="font-medium text-gray-200">{formatTeamName(game.homeTeam)}</span>
                </div>
              </div>
              {game.homeScore !== undefined && isCompleted && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: internalShowScores ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`${internalShowScores ? 'visible' : 'invisible'} font-bold text-xl text-gray-200`}
                >
                  {game.homeScore}
                </motion.div>
              )}
            </div>
          </div>
          
          {/* Game details */}
          <div className="pt-2 border-t border-gray-700">
            <div className="flex flex-col gap-1 mt-1">
              {game.arena !== "Unknown" && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-400">{game.arena}</span>
                </div>
              )}
              
              {/* Show local time if available */}
              {localTime && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-400">{localTime} local time</span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={isSaved}
            onClick={() => saveGame(game)}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-indigo-400/25 bg-indigo-500/10 px-4 text-sm font-semibold text-indigo-100 transition-colors hover:bg-indigo-500/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-300 disabled:cursor-default disabled:border-emerald-400/20 disabled:bg-emerald-500/10 disabled:text-emerald-200"
          >
            {isSaved ? <BookmarkCheck className="h-4 w-4" aria-hidden="true" /> : <BookmarkPlus className="h-4 w-4" aria-hidden="true" />}
            {isSaved ? 'Saved to My Replay Plan' : 'Save to My Replay Plan'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GameCard;
